import { z } from 'zod';
import {
  BrandedId,
  ExtensionMinutesSchema,
  IsoInstant,
  PartySizeSchema,
  ScenarioTypeSchema,
  StrategySchema,
} from './common.js';

export const ReservationStatusSchema = z.enum([
  'pending',
  'confirmed',
  'checked_in',
  'completed',
  'canceled',
  'no_show',
]);

// POST /api/staff/reservations
export const CreateReservationBody = z.object({
  venueId: BrandedId,
  serviceId: BrandedId,
  scenarioType: ScenarioTypeSchema,
  partySize: PartySizeSchema,
  startTime: IsoInstant,
  endTime: IsoInstant,
  source: z.string().min(1),
  customerRef: z.string().nullable(),
  customerId: BrandedId.nullish(),
  notes: z.string().nullable(),
});
export type CreateReservationBody = z.infer<typeof CreateReservationBody>;

export const CreateReservationResponse = z.object({
  reservationId: BrandedId,
  status: ReservationStatusSchema,
});

// POST /api/staff/reservations/suggest-availability
export const SuggestAvailabilityBody = z.object({
  venueId: BrandedId,
  serviceId: BrandedId,
  scenarioType: ScenarioTypeSchema,
  resourceId: BrandedId,
  partySize: PartySizeSchema,
  startTime: IsoInstant,
  endTime: IsoInstant,
});
export type SuggestAvailabilityBody = z.infer<typeof SuggestAvailabilityBody>;

export const CandidateSegmentSchema = z.object({
  startPosition: z.number().int().positive(),
  endPosition: z.number().int().positive(),
  peopleCapacity: z.number().int().positive(),
  bufferCapacity: z.number().int().nonnegative(),
  totalCapacity: z.number().int().positive(),
  score: z.number(),
  strategyName: StrategySchema,
});

export const SuggestAvailabilityResponse = z.object({
  candidateSegments: z.array(CandidateSegmentSchema),
  strategy: StrategySchema,
  bufferApplied: z.number().int().nonnegative(),
});

// POST /api/staff/reservations/:id/allocate
export const AllocateReservationBody = z.object({
  resourceId: BrandedId,
  segment: z.object({
    startPosition: z.number().int().positive(),
    endPosition: z.number().int().positive(),
  }),
});
export type AllocateReservationBody = z.infer<typeof AllocateReservationBody>;

export const AllocateReservationResponse = z.object({
  allocationId: BrandedId,
  allocatedUnits: z.array(z.number().int().positive()),
  bufferApplied: z.number().int().nonnegative(),
});

// POST /api/staff/reservations/:id/extend
export const ExtendReservationBody = z.object({
  extensionMinutes: ExtensionMinutesSchema,
});
export type ExtendReservationBody = z.infer<typeof ExtendReservationBody>;

export const ExtendReservationResponse = z.object({
  reservationId: BrandedId,
  newEndTime: IsoInstant,
});

// POST /api/staff/reservations/:id/cancel
export const CancelReservationBody = z.object({
  reason: z.string().nullable(),
});
export const CancelReservationResponse = z.object({
  reservationId: BrandedId,
  status: z.literal('canceled'),
});

// POST /api/staff/reservations/:id/check-in
export const CheckInReservationResponse = z.object({
  reservationId: BrandedId,
  status: z.literal('checked_in'),
});

// POST /api/staff/reservations/:id/complete
export const CompleteReservationResponse = z.object({
  reservationId: BrandedId,
  status: z.literal('completed'),
});

// GET /api/staff/reservations/:id
export const ReservationDetailView = z.object({
  reservationId: BrandedId,
  status: ReservationStatusSchema,
  scenarioType: ScenarioTypeSchema,
  partySize: PartySizeSchema,
  startTime: IsoInstant,
  endTime: IsoInstant,
  source: z.string(),
  allocation: z
    .object({
      allocationId: BrandedId,
      resourceId: BrandedId,
      startPosition: z.number().int().positive(),
      endPosition: z.number().int().positive(),
      bufferApplied: z.number().int().nonnegative(),
    })
    .nullable(),
});

// GET /api/staff/dashboard (today reservations)
export const TodayReservationRow = z.object({
  reservationId: BrandedId,
  startTime: IsoInstant,
  endTime: IsoInstant,
  partySize: PartySizeSchema,
  status: ReservationStatusSchema,
});

// GET /api/staff/audit-logs
export const AuditLogRowSchema = z
  .object({
    auditId: BrandedId,
    action: z.string(),
    actorId: z.string(),
    actorRole: z.enum(['guest', 'staff', 'manager', 'system']),
    targetType: z.string(),
    targetId: z.string(),
    payload: z.record(z.unknown()),
    createdAtIso: IsoInstant,
  })
  .strict();
export type AuditLogRow = z.infer<typeof AuditLogRowSchema>;

export const ListAuditLogsResponse = z
  .object({
    items: z.array(AuditLogRowSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();
export type ListAuditLogsResponse = z.infer<typeof ListAuditLogsResponse>;

// PUT /api/staff/reservations/:id (edit partySize + notes)
export const UpdateReservationBody = z
  .object({
    partySize: z.number().int().positive().optional(),
    notes: z.string().max(500).nullable().optional(),
  })
  .strict()
  .refine((v) => v.partySize !== undefined || v.notes !== undefined, {
    message: 'at least one field required',
  });
export type UpdateReservationBody = z.infer<typeof UpdateReservationBody>;

export const UpdateReservationResponse = z
  .object({
    reservationId: BrandedId,
    partySize: PartySizeSchema,
    notes: z.string().nullable(),
    status: ReservationStatusSchema,
  })
  .strict();
export type UpdateReservationResponse = z.infer<typeof UpdateReservationResponse>;

// POST /api/staff/reservations/:id/record-payment
export const PaymentStatusSchema = z.enum(['unpaid', 'partial', 'paid', 'refunded']);
export const CurrencySchema = z.enum(['TWD']);

export const RecordReservationPaymentBody = z
  .object({
    paidAmount: z.number().int().positive(),
    currency: CurrencySchema,
    paidAt: IsoInstant,
  })
  .strict();
export type RecordReservationPaymentBody = z.infer<typeof RecordReservationPaymentBody>;

export const RecordReservationPaymentResponse = z
  .object({
    reservationId: BrandedId,
    totalAmount: z.number().int().nonnegative().nullable(),
    paidAmount: z.number().int().positive(),
    currency: CurrencySchema,
    paymentStatus: PaymentStatusSchema,
    paidAt: IsoInstant,
  })
  .strict();
export type RecordReservationPaymentResponse = z.infer<typeof RecordReservationPaymentResponse>;

// GET /api/staff/reservations (paginated list with filters)
export const ReservationListRowSchema = z
  .object({
    reservationId: BrandedId,
    serviceId: BrandedId,
    scenarioType: ScenarioTypeSchema,
    status: ReservationStatusSchema,
    partySize: PartySizeSchema,
    startTime: IsoInstant,
    endTime: IsoInstant,
    source: z.string(),
    customerRef: z.string().nullable(),
    customerId: BrandedId.nullable(),
    customerName: z.string().nullable(),
    notes: z.string().nullable(),
    totalAmount: z.number().int().nonnegative().nullable(),
    paidAmount: z.number().int().nonnegative().nullable(),
    currency: CurrencySchema.nullable(),
    paymentStatus: PaymentStatusSchema,
    paidAt: IsoInstant.nullable(),
  })
  .strict();
export type ReservationListRow = z.infer<typeof ReservationListRowSchema>;

export const ListReservationsResponse = z
  .object({
    items: z.array(ReservationListRowSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();
export type ListReservationsResponse = z.infer<typeof ListReservationsResponse>;

// GET /api/staff/resource-occupancy
export const OccupancySlot = z.object({
  position: z.number().int().positive(),
  label: z.string(),
  active: z.boolean(),
  occupiedByReservationId: BrandedId.nullable(),
  isBuffer: z.boolean(),
});

export const ResourceOccupancyResponse = z.object({
  resourceId: BrandedId,
  slots: z.array(OccupancySlot),
});

// GET /api/staff/service-constraints?venueId=...&serviceId=...
export const ServiceConstraintsResponse = z.object({
  serviceId: BrandedId,
  serviceName: z.string(),
  scenarioType: ScenarioTypeSchema,
  active: z.boolean(),
  durationMinMinutes: z.number().int().positive(),
  durationMaxMinutes: z.number().int().positive(),
  leadTimeMinutes: z.number().int().nonnegative(),
  minPartySize: z.number().int().positive(),
  maxPartySize: z.number().int().positive(),
  blackoutWindows: z.array(
    z.object({
      start: IsoInstant,
      end: IsoInstant,
    }),
  ),
  overbookAllowed: z.boolean(),
  splitAllowed: z.boolean(),
  extensionAllowed: z.boolean(),
});
export type ServiceConstraintsResponse = z.infer<typeof ServiceConstraintsResponse>;

// GET /api/staff/venue-zones?venueId=...
export const PricingUnitSchema = z.enum([
  'per_hour_per_person',
  'per_hour_per_seat',
  'flat_per_hour',
  'tiered_per_person',
]);
export const PricingCurrencySchema = z.enum(['TWD']);
export const AllocationModeSchema = z.enum(['linear_capacity', 'exclusive_zone']);

export const PartySizeTierSchema = z.object({
  minPartySize: z.number().int().positive(),
  maxPartySize: z.number().int().positive().nullable(),
  amountPerPersonPerHour: z.number().int().positive(),
});

export const ZonePricingSchema = z.object({
  amount: z.number().nonnegative(),
  unit: PricingUnitSchema,
  currency: PricingCurrencySchema,
  tiers: z.array(PartySizeTierSchema).nullable().optional(),
});

export const ZoneSummarySchema = z.object({
  resourceId: BrandedId,
  serviceId: BrandedId,
  scenarioType: ScenarioTypeSchema,
  zoneLabel: z.string().min(1),
  serviceName: z.string().min(1),
  pricing: ZonePricingSchema.nullable(),
  unitCapacityMin: z.number().int().positive(),
  unitCapacityMax: z.number().int().positive(),
  totalUnits: z.number().int().nonnegative(),
  minPartySize: z.number().int().positive(),
  maxPartySize: z.number().int().positive(),
  allocationMode: AllocationModeSchema.default('linear_capacity'),
});

export const VenueZonesResponse = z.object({
  venueId: BrandedId,
  zones: z.array(ZoneSummarySchema),
});
export type VenueZonesResponse = z.infer<typeof VenueZonesResponse>;
export type ZoneSummary = z.infer<typeof ZoneSummarySchema>;
export type ZonePricing = z.infer<typeof ZonePricingSchema>;

// GET /api/staff/analytics/reservations
export const StatusCountsSchema = z.object({
  pending: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  checked_in: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  canceled: z.number().int().nonnegative(),
  no_show: z.number().int().nonnegative(),
});

export const DailyCountBucketSchema = z.object({
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  total: z.number().int().nonnegative(),
  byStatus: StatusCountsSchema,
});

export const PartySizeBucketSchema = z.object({
  min: z.number().int().positive(),
  max: z.number().int().positive().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const ReservationAnalyticsResponse = z.object({
  totalReservations: z.number().int().nonnegative(),
  activeReservations: z.number().int().nonnegative(),
  expectedGuests: z.number().int().nonnegative(),
  canceledCount: z.number().int().nonnegative(),
  noShowCount: z.number().int().nonnegative(),
  cancelRatePct: z.number().int().nonnegative(),
  daily: z.array(DailyCountBucketSchema),
  partySizeBuckets: z.array(PartySizeBucketSchema),
  statusTotals: StatusCountsSchema,
});
export type ReservationAnalyticsResponse = z.infer<typeof ReservationAnalyticsResponse>;

// GET /api/staff/venue-info + PUT /api/staff/venue-info
export const VenueStatusSchema = z.enum(['active', 'suspended']);

export const VenueInfoResponse = z
  .object({
    venueId: BrandedId,
    name: z.string().min(1),
    timezone: z.string().min(1),
    status: VenueStatusSchema,
  })
  .strict();
export type VenueInfoResponse = z.infer<typeof VenueInfoResponse>;

export const UpdateVenueInfoBody = z
  .object({
    name: z.string().trim().min(1).max(100),
    timezone: z.string().trim().min(1).max(64),
    status: VenueStatusSchema,
  })
  .strict();
export type UpdateVenueInfoBody = z.infer<typeof UpdateVenueInfoBody>;

// PUT /api/staff/services/:serviceId/info
export const UpdateServiceInfoBody = z
  .object({
    name: z.string().trim().min(1).max(100),
    active: z.boolean(),
  })
  .strict();
export type UpdateServiceInfoBody = z.infer<typeof UpdateServiceInfoBody>;

export const UpdateServiceInfoResponse = z
  .object({
    serviceId: BrandedId,
    name: z.string(),
    active: z.boolean(),
  })
  .strict();

// PUT /api/staff/services/:serviceId/policy
export const BlackoutWindowSchema = z
  .object({
    start: z.string().datetime(),
    end: z.string().datetime(),
  })
  .strict();
export type BlackoutWindowDto = z.infer<typeof BlackoutWindowSchema>;

export const UpdateServicePolicyBody = z
  .object({
    minPartySize: z.number().int().min(1),
    maxPartySize: z.number().int().min(1),
    durationMinMinutes: z.number().int().positive(),
    durationMaxMinutes: z.number().int().positive(),
    leadTimeMinutes: z.number().int().nonnegative(),
    blackoutWindows: z.array(BlackoutWindowSchema).max(50),
    overbookAllowed: z.boolean(),
    splitAllowed: z.boolean(),
    extensionAllowed: z.boolean(),
  })
  .strict();
export type UpdateServicePolicyBody = z.infer<typeof UpdateServicePolicyBody>;

export const UpdateServicePolicyResponse = z
  .object({
    serviceId: BrandedId,
    policySetId: BrandedId,
    version: z.number().int().positive(),
  })
  .strict();

// GET /api/staff/venue-zones/:resourceId/units
export const ResourceUnitDtoSchema = z
  .object({
    unitId: BrandedId,
    resourceId: BrandedId,
    position: z.number().int().positive(),
    label: z.string(),
    capacity: z.number().int().positive(),
    active: z.boolean(),
    minCapacity: z.number().int().positive().nullable(),
  })
  .strict();
export type ResourceUnitDto = z.infer<typeof ResourceUnitDtoSchema>;

export const ListResourceUnitsResponse = z
  .object({
    units: z.array(ResourceUnitDtoSchema),
  })
  .strict();

// PUT /api/staff/resource-units/:unitId
export const UpdateResourceUnitBody = z
  .object({
    label: z.string().max(50),
    capacity: z.number().int().positive(),
    active: z.boolean(),
    minCapacity: z.number().int().positive().nullable(),
  })
  .strict();
export type UpdateResourceUnitBody = z.infer<typeof UpdateResourceUnitBody>;

export const UpdateResourceUnitResponse = ResourceUnitDtoSchema;

// PUT /api/staff/venue-zones/:resourceId/metadata
export const UpdateZoneMetadataBody = z
  .object({
    zoneLabel: z.string().trim().min(1).max(50),
    capacity: z.number().int().positive(),
    active: z.boolean(),
  })
  .strict();
export type UpdateZoneMetadataBody = z.infer<typeof UpdateZoneMetadataBody>;

export const UpdateZoneMetadataResponse = z
  .object({
    resourceId: BrandedId,
    zoneLabel: z.string(),
    capacity: z.number().int().positive(),
    active: z.boolean(),
  })
  .strict();

// PUT /api/staff/venue-zones/:resourceId/pricing
export const UpdateZonePricingBody = z
  .object({
    pricing: ZonePricingSchema.nullable(),
  })
  .strict();
export type UpdateZonePricingBody = z.infer<typeof UpdateZonePricingBody>;

export const UpdateZonePricingResponse = z
  .object({
    resourceId: BrandedId,
    pricing: ZonePricingSchema.nullable(),
  })
  .strict();

// ── Venue settings (§Region B) ──────────────────────────────────────────
// Secret inputs always *plaintext* over HTTPS; handler encrypts server-side.
// Response never exposes plaintext — only `configured` + `last4` metadata.

const NullableSecret = z.string().min(1).nullable().optional();

export const UpsertVenueNotificationSettingsBody = z
  .object({
    line: z
      .object({
        channelAccessToken: NullableSecret,
        channelSecret: NullableSecret,
      })
      .strict()
      .optional(),
    email: z
      .object({
        provider: z.enum(['brevo']).nullable().optional(),
        apiKey: NullableSecret,
        senderEmail: z.string().email().nullable().optional(),
        senderName: z.string().min(1).max(80).nullable().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type UpsertVenueNotificationSettingsBody = z.infer<
  typeof UpsertVenueNotificationSettingsBody
>;

export const UpsertVenueAutoRulesBody = z
  .object({
    vipVisitCountThreshold: z.number().int().min(1).max(1000).optional(),
    blacklistNoShowThreshold: z.number().int().min(1).max(100).optional(),
    publicReservationLeadTimeMinutes: z.number().int().min(0).max(60 * 24 * 30).optional(),
  })
  .strict();
export type UpsertVenueAutoRulesBody = z.infer<typeof UpsertVenueAutoRulesBody>;

export const UpsertVenueRateLimitBody = z
  .object({
    publicReservationCapacity: z.number().int().min(1).max(10_000).optional(),
    publicReservationRefillPerSec: z.number().min(0.0001).max(1_000).optional(),
  })
  .strict();
export type UpsertVenueRateLimitBody = z.infer<typeof UpsertVenueRateLimitBody>;

export const UpsertVenueCapabilitiesBody = z
  .object({
    overrides: z.record(z.unknown()),
  })
  .strict();
export type UpsertVenueCapabilitiesBody = z.infer<typeof UpsertVenueCapabilitiesBody>;

// ── POS + payment (§Region J) ───────────────────────────────────────────
const PaymentMethodSchema = z.enum([
  'manual_cash',
  'manual_transfer',
  'credit_card',
  'line_pay',
  'apple_pay',
  'google_pay',
]);

export const OpenOrderBody = z
  .object({
    reservationId: z.string().min(1).nullable().optional(),
    customerId: z.string().min(1).nullable().optional(),
  })
  .strict();
export type OpenOrderBody = z.infer<typeof OpenOrderBody>;

export const AddOrderItemBody = z
  .object({
    menuItemId: z.string().min(1),
    quantity: z.number().int().min(1).max(99),
    note: z.string().min(1).max(200).nullable().optional(),
    modifiers: z.array(z.string().min(1).max(40)).max(20).optional(),
  })
  .strict();
export type AddOrderItemBody = z.infer<typeof AddOrderItemBody>;

export const SubmitOrderBody = z
  .object({
    tipAmount: z.number().int().min(0).max(1_000_000).nullable().optional(),
    serviceChargeOverride: z.number().int().min(0).max(1_000_000).nullable().optional(),
  })
  .strict();
export type SubmitOrderBody = z.infer<typeof SubmitOrderBody>;

export const VoidOrderBody = z
  .object({
    reason: z.string().min(1).max(200),
  })
  .strict();
export type VoidOrderBody = z.infer<typeof VoidOrderBody>;

export const CreateExpenseBody = z
  .object({
    category: z.enum([
      'rent',
      'hardware',
      'salary',
      'insurance',
      'software',
      'utility',
      'ingredient',
      'supplies',
      'marketing',
      'bonus',
      'other',
    ]),
    kind: z.enum(['fixed', 'variable']).optional(),
    amount: z.number().int().min(1).max(1_000_000_000),
    recurrence: z.enum(['one_off', 'weekly', 'monthly', 'yearly']),
    description: z.string().min(1).max(300).nullable().optional(),
    effectiveFromIso: z.string().min(10),
  })
  .strict();
export type CreateExpenseBody = z.infer<typeof CreateExpenseBody>;

export const EndDateExpenseBody = z.object({ endDateIso: z.string().min(10) }).strict();
export type EndDateExpenseBody = z.infer<typeof EndDateExpenseBody>;

// ── Play session (玩樂結帳) ─────────────────────────────────────────
// Strategy + rounding params are stored as JSON snapshots; schema mirrors
// the union in modules/play_session/domain/services/pricing-strategies.
export const StartPlaySessionBody = z
  .object({
    zoneId: z.string().min(1),
    partySize: z.number().int().min(1).max(200),
    reservationId: z.string().min(1).nullable().optional(),
    customerId: z.string().min(1).nullable().optional(),
    rateSnapshot: z
      .object({
        strategy: z
          .object({
            type: z.enum([
              'hourly_per_person',
              'hourly_per_seat',
              'flat_per_hour',
              'tiered_per_person',
              'package_block',
            ]),
            // opaque per-strategy params — validated by domain on construct
            params: z.record(z.unknown()),
          })
          .strict(),
        rounding: z
          .object({
            type: z.enum([
              'unit_hour_tolerant',
              'quarter_up',
              'half_up',
              'whole_hour_up',
              'per_minute',
            ]),
            params: z.record(z.unknown()),
          })
          .strict(),
        currency: z.literal('TWD'),
      })
      .strict(),
  })
  .strict();
export type StartPlaySessionBody = z.infer<typeof StartPlaySessionBody>;

export const CancelPlaySessionBody = z
  .object({ reason: z.string().min(1).max(200) })
  .strict();
export type CancelPlaySessionBody = z.infer<typeof CancelPlaySessionBody>;

export const UpsertMenuItemBody = z
  .object({
    menuItemId: z.string().min(1).optional(),
    displayName: z.string().min(1).max(80),
    unitPrice: z.number().int().min(0).max(1_000_000_000),
    category: z.string().min(1).max(40).nullable().optional(),
    sortOrder: z.number().int().min(0).max(100000).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertMenuItemBody = z.infer<typeof UpsertMenuItemBody>;

export const RefundOrderBody = z
  .object({
    amount: z.number().int().min(1).max(1_000_000_000).optional(),
    reason: z.string().min(1).max(200).nullable().optional(),
  })
  .strict();
export type RefundOrderBody = z.infer<typeof RefundOrderBody>;

export const ApplyDiscountBody = z
  .object({
    mode: z.enum(['fixed', 'percent']),
    fixedAmount: z.number().int().min(1).max(1_000_000_000).optional(),
    percent: z.number().min(0.01).max(100).optional(),
    reason: z.string().min(1).max(120),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.mode === 'fixed' && typeof v.fixedAmount !== 'number') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'fixed mode requires fixedAmount', path: ['fixedAmount'] });
    }
    if (v.mode === 'percent' && typeof v.percent !== 'number') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'percent mode requires percent', path: ['percent'] });
    }
  });
export type ApplyDiscountBody = z.infer<typeof ApplyDiscountBody>;

export const AuthorizeOrderPaymentBody = z
  .object({
    method: PaymentMethodSchema,
    preferredProvider: z.string().min(1).optional(),
    providerPayload: z.record(z.unknown()).optional(),
    expectedAmount: z.number().int().min(0).max(1_000_000_000),
  })
  .strict();
export type AuthorizeOrderPaymentBody = z.infer<typeof AuthorizeOrderPaymentBody>;

// ── Staff roster (§Region F RBAC) ───────────────────────────────────────
const StaffRoleSchema = z.enum(['staff', 'manager', 'admin']);

export const InviteStaffBody = z
  .object({
    email: z.string().email(),
    displayName: z.string().min(1).max(80),
    role: StaffRoleSchema,
  })
  .strict();
export type InviteStaffBody = z.infer<typeof InviteStaffBody>;

export const SetStaffRoleBody = z.object({ role: StaffRoleSchema }).strict();
export type SetStaffRoleBody = z.infer<typeof SetStaffRoleBody>;

const MaxBrand = z.string().min(1).max(80);
export const UpsertVenueBrandingBody = z
  .object({
    branding: z
      .object({
        appTitle: MaxBrand.optional(),
        tagline: MaxBrand.optional(),
        tileUnitLabel: z.string().min(1).max(6).optional(),
        availabilityHeadline: MaxBrand.optional(),
        availabilityFootnote: z.string().min(1).max(200).optional(),
        availableWording: MaxBrand.optional(),
        fullWording: MaxBrand.optional(),
        bookingTerm: z.string().min(1).max(6).optional(),
        slotTerm: z.string().min(1).max(6).optional(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type UpsertVenueBrandingBody = z.infer<typeof UpsertVenueBrandingBody>;

export const TestLinePushBody = z
  .object({
    recipientUserId: z
      .string()
      .regex(/^U[0-9a-f]{32}$/, { message: '必須是 LINE userId（U 開頭 + 32 hex）' }),
    message: z.string().min(1).max(1000).optional(),
  })
  .strict();
export type TestLinePushBody = z.infer<typeof TestLinePushBody>;
